import React, { useState } from 'react';
import { Tv, MonitorPlay, Cast, X, Loader2 } from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';

/**
 * V8.8.6 — Um perfil, um lugar.
 * O perfil está tocando numa TV (Cast) e este aparelho tentou dar play:
 *  - Assistir aqui e fechar a outra tela → para a TV e abre aqui;
 *  - Enviar para a TV → o que foi escolhido vai para a TV (este aparelho vira controle);
 *  - Cancelar.
 */
export const ProfileOnTvModal: React.FC = () => {
  const { profileOnTv, watchHereAndCloseTv, sendPendingToTv, cancelProfileOnTv } = useIPTV();
  const [busy, setBusy] = useState(false);

  if (!profileOnTv) return null;

  const btn =
    'w-full h-12 px-4 rounded-xl text-sm font-bold transition cursor-pointer flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-theme-accent';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-md p-3 select-none">
      <div className="bg-zinc-950 border border-zinc-800 rounded-2xl sm:rounded-3xl max-w-md w-full shadow-2xl overflow-hidden">
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-800/80 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-theme-accent flex items-center justify-center text-black shadow-md shadow-theme-accent-sm shrink-0">
              <Tv className="w-5 h-5 stroke-[2.5]" />
            </div>
            <span className="font-black text-sm sm:text-base text-white tracking-wider">OPEN TV</span>
          </div>
          <button
            onClick={cancelProfileOnTv}
            className="w-9 h-9 rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-300 hover:text-white transition cursor-pointer flex items-center justify-center"
            title="Cancelar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          <div>
            <h3 className="text-base sm:text-lg font-black text-white leading-snug">
              Seu perfil está reproduzindo em outra tela
            </h3>
            <div className="mt-2 flex items-center gap-2.5 p-2.5 rounded-xl bg-zinc-900/80 border border-zinc-800 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-zinc-800 text-theme-accent flex items-center justify-center shrink-0">
                <MonitorPlay className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-white truncate">{profileOnTv.tvName}</p>
                {profileOnTv.title && <p className="text-[11px] text-zinc-400 truncate">{profileOnTv.title}</p>}
              </div>
            </div>
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed">Um perfil só reproduz em um lugar por vez.</p>
          </div>

          <div className="space-y-2">
            <button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                await watchHereAndCloseTv();
                setBusy(false);
              }}
              className={`${btn} bg-theme-accent hover:opacity-90 text-black font-black shadow-md shadow-theme-accent-sm`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MonitorPlay className="w-4 h-4" />}
              Assistir aqui e fechar a outra tela
            </button>
            <button disabled={busy} onClick={sendPendingToTv} className={`${btn} bg-zinc-800/90 hover:bg-zinc-700 text-zinc-100`}>
              <Cast className="w-4 h-4" />
              Enviar para a {profileOnTv.tvName}
            </button>
            <button disabled={busy} onClick={cancelProfileOnTv} className={`${btn} bg-transparent hover:bg-zinc-900 text-zinc-400`}>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
